import { KeyValue } from '@angular/common';
import {
  Component,
  Input,
  OnInit,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import { SnAlarm } from '@lib/api/entities';
import { t } from '@lib/translation';

import { AlprDetectionService, BoundingBox } from '../../../services/alpr/alpr-detection.service';
import { LprPlateImageDialogComponent } from '../../lpr-plate-image-dialog/lpr-plate-image-dialog.component';

@Component({
  selector: 'fs-alpr-media-card',
  templateUrl: './alpr-media-card.component.html',
  styleUrls: ['./alpr-media-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AlprMediaCardComponent implements OnInit {
  @Input() alarm!: SnAlarm;
  @Input() hideActions: boolean = false;
  @Output() enlargeImage = new EventEmitter<void>(); // Event to notify parent of fullscreen click

  public isFlipped: boolean = false;
  public contextImageLoadFailed = false;

  constructor(
    private readonly _alprDetection: AlprDetectionService,
    private readonly _matDialog: MatDialog,
    private readonly _cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    // Component initialization
  }

  // Flip animation method
  public turn(): void {
    this.isFlipped = !this.isFlipped;
    this._cdr.markForCheck();
  }

  // ALPR-specific methods
  public getAlprContextImage(): string | null {
    if (!this._alprDetection.isAlprAlarm(this.alarm)) {
      return null;
    }
    const alprDetails = this._alprDetection.getAlprDetails(this.alarm);
    return (alprDetails.contextImage as string) || null;
  }

  public onContextImageError(): void {
    this.contextImageLoadFailed = true;
    this._cdr.markForCheck();
  }

  public hasValidBoundingBoxData(): boolean {
    if (!this._alprDetection.isAlprAlarm(this.alarm)) {
      return false;
    }
    const alprDetails = this._alprDetection.getAlprDetails(this.alarm);
    const plateBoundingBox = alprDetails.plateBoundingBox;
    const imageCoordinates = alprDetails.imageCoordinates;

    if (!plateBoundingBox || !imageCoordinates) {
      return false;
    }

    // Validate plate bounding box
    if (
      typeof plateBoundingBox.x !== 'number' ||
      isNaN(plateBoundingBox.x) ||
      typeof plateBoundingBox.y !== 'number' ||
      isNaN(plateBoundingBox.y) ||
      typeof plateBoundingBox.width !== 'number' ||
      isNaN(plateBoundingBox.width) ||
      typeof plateBoundingBox.height !== 'number' ||
      isNaN(plateBoundingBox.height)
    ) {
      return false;
    }

    // Validate image coordinates
    if (
      typeof imageCoordinates.x !== 'number' ||
      isNaN(imageCoordinates.x) ||
      typeof imageCoordinates.y !== 'number' ||
      isNaN(imageCoordinates.y) ||
      typeof imageCoordinates.width !== 'number' ||
      isNaN(imageCoordinates.width) ||
      typeof imageCoordinates.height !== 'number' ||
      isNaN(imageCoordinates.height)
    ) {
      return false;
    }

    return (
      imageCoordinates.width > 0 &&
      imageCoordinates.height > 0 &&
      plateBoundingBox.width > 0 &&
      plateBoundingBox.height > 0
    );
  }

  public getBoundingBoxStyling(): { [key: string]: string } | null {
    if (!this.hasValidBoundingBoxData()) {
      return null;
    }

    const alprDetails = this._alprDetection.getAlprDetails(this.alarm);
    const plateBoundingBox = alprDetails.plateBoundingBox!;
    const imageCoordinates = alprDetails.imageCoordinates!;

    return {
      left: `${((plateBoundingBox.x - imageCoordinates.x) / imageCoordinates.width) * 100}%`,
      top: `${((plateBoundingBox.y - imageCoordinates.y) / imageCoordinates.height) * 100}%`,
      width: `${(plateBoundingBox.width / imageCoordinates.width) * 100}%`,
      height: `${(plateBoundingBox.height / imageCoordinates.height) * 100}%`,
    };
  }

  public openEnlargedImageDialog(): void {
    this._matDialog.open(LprPlateImageDialogComponent, {
      data: this.alarm,
      width: '45vw',
      panelClass: 'lpr-plate__dialog-container',
    });
    this.enlargeImage.emit(); // Notify parent if needed
  }

  // Helper methods from alarm-card pattern
  public isObject(item: unknown): boolean {
    return Object.prototype.toString.call(item).indexOf('Object') > -1;
  }

  public isArray(item: unknown): boolean {
    return Array.isArray(item);
  }

  public trackDetailItem(index: number, item: KeyValue<string, unknown>): string {
    return item.key;
  }

  public originalOrder = (a: KeyValue<number, string>, b: KeyValue<number, string>): number => {
    return 0;
  };

  public formatDataKeys(data: Record<string, unknown>[]): Record<string, unknown>[] {
    const formattedData: Record<string, unknown>[] = [];
    data.forEach((item: Record<string, unknown>) => {
      formattedData.push(
        Object.keys(item).reduce((accumulator: Record<string, unknown>, key) => {
          accumulator[key.toLowerCase()] = item[key];
          return accumulator;
        }, {}),
      );
    });
    return formattedData;
  }

  public canSeeDetail(type: string, key: string): boolean {
    const importantDetails: Record<string, string[]> = {
      'rt alpr': ['reason', 'Plate', 'list', 'plateState', 'make', 'model', 'color'],
    };

    const hiddenDetails: Record<string, string[]> = {
      'amber alert': [
        'isOpen',
        'isPreview',
        'messageList',
        'orgName',
        'timeZone',
        'personList',
        'targetList',
        'vehicleList',
      ],
      dronesense: ['videoUrl'],
    };

    const important = importantDetails[type.toLowerCase()] || [];
    const hidden = hiddenDetails[type.toLowerCase()] || [];

    return important.indexOf(key) < 0 && hidden.indexOf(key) < 0;
  }

  public isTimestamp(value: unknown): boolean {
    if (!value) return false;
    const date = new Date(value as string | number | Date);
    return !isNaN(date.getTime());
  }
}
